import {
  LoadingPage,
  LoginFailedPage,
  ResumeApplicationLoginPage,
} from '@resurs-internal/deposits-app-components';
import { useCallback, useContext, useEffect, useState } from 'react';
import {
  Outlet,
  useLocation,
  useNavigate,
  useSearchParams,
} from 'react-router';

import { CountryContext } from '@/contexts';
import {
  authReturnPath,
  isAuthenticated,
  logout,
  setAuthSessionFromCode,
} from '@/helpers';
import { useProducts } from '@/hooks';
import { authenticateUser } from '@/services';

export const LoginCallback = () => {
  const { country } = useContext(CountryContext);
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const { isLoading: isLoadingProducts, productFromCode } =
    useProducts(country);
  const [isLoading, setIsLoading] = useState(true);

  const productCode = searchParams.get('state');
  const code = searchParams.get('code');
  const error = searchParams.get('error');
  const errorDescription = searchParams.get('error_description');

  const onError = useCallback(() => {
    logout().finally(() => setIsLoading(false));
  }, []);

  const handleNavigateForward = useCallback(() => {
    const params = new URLSearchParams();

    params.set('state', productCode || '');
    const currentCampaignCode = searchParams.get('campaignCode');

    if (currentCampaignCode) {
      params.set('campaignCode', currentCampaignCode);
    }

    const configurationRuleId = searchParams.get('configurationRuleId');

    if (configurationRuleId) {
      params.set('configurationRuleId', configurationRuleId);
    }

    navigate(
      {
        pathname: `../${location.pathname}`,
        search: params.toString(),
      },
      { replace: true },
    );
  }, [location.pathname, navigate, productCode, searchParams]);

  useEffect(() => {
    if (isAuthenticated()) {
      setIsLoading(false);
    } else if (error) onError();
    else if (code && productCode !== null) {
      setAuthSessionFromCode(code, productCode)
        .then(handleNavigateForward)
        .catch(onError);
    } else {
      setIsLoading(false);
    }
  }, [code, error, handleNavigateForward, onError, productCode]);

  useEffect(() => {
    const onStorageChange = () => {
      if (isAuthenticated()) setIsLoading(false);
      else onError();
    };

    window.addEventListener('storage', onStorageChange);

    return () => window.removeEventListener('storage', onStorageChange);
  }, [onError]);

  const handleAuthenticateUser = (productCode?: string) => {
    authenticateUser(
      authReturnPath(window.location.pathname, searchParams),
      productCode || '',
      country,
    );
  };

  if (isLoading || isLoadingProducts) return <LoadingPage country={country} />;
  if (!isAuthenticated())
    if (error) {
      return (
        <LoginFailedPage
          authenticateUser={handleAuthenticateUser}
          country={country}
          error={error}
          errorDescription={errorDescription ?? undefined}
          isAuthenticated={isAuthenticated()}
          navigateForward={handleNavigateForward}
          product={productFromCode(productCode ?? '')}
        />
      );
    } else {
      return (
        <ResumeApplicationLoginPage
          authenticateUser={handleAuthenticateUser}
          country={country}
          isAuthenticated={isAuthenticated()}
          navigateForward={handleNavigateForward}
          product={productFromCode(productCode ?? '')}
        />
      );
    }

  return <Outlet context={{ product: productFromCode(productCode ?? '') }} />;
};
